class MainCart extends HTMLElement {
  constructor() {
    super();

    this.quantityTimers = new Map();
    this.timerInterval = null;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);

    this.syncAllQuantityButtons();
    this.initializeTimer();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);

    this.quantityTimers.forEach((timer) => window.clearTimeout(timer));
    this.quantityTimers.clear();

    if (this.timerInterval) {
      window.clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  async onClick(event) {
    if (!(event.target instanceof Element)) return;

    const quantityButton = event.target.closest('[data-cart-quantity-button]');
    if (quantityButton) {
      event.preventDefault();
      this.handleQuantityButton(quantityButton);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();

      const item = removeButton.closest('[data-cart-item]');
      if (item) {
        await this.changeLine(item.dataset.lineKey, 0, removeButton);
      }
      return;
    }

    const removeDiscountButton = event.target.closest('[data-cart-discount-remove]');
    if (removeDiscountButton) {
      event.preventDefault();
      await this.removeDiscount(removeDiscountButton);
      return;
    }

    const checkoutButton = event.target.closest('[data-cart-checkout]');
    if (checkoutButton) {
      if (!this.validateTerms()) {
        event.preventDefault();
        return;
      }

      const discountInput = this.querySelector('[data-cart-discount-input]');
      if (discountInput && discountInput.value.trim()) {
        event.preventDefault();
        const discountForm = this.querySelector('[data-cart-discount-form]');
        this.applyDiscount(discountForm).then(() => {
          const mainForm = this.querySelector('[data-cart-form]');
          if (mainForm) mainForm.submit();
        });
      }
    }
  }

  onChange(event) {
    if (!(event.target instanceof Element)) return;

    const quantityInput = event.target.closest('[data-cart-quantity]');
    if (!quantityInput) return;

    this.handleQuantityInput(quantityInput);
  }

  async onSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;

    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      event.preventDefault();
      await this.applyDiscount(discountForm);
    }
  }

  handleQuantityButton(button) {
    if (button.disabled) return;

    const item = button.closest('[data-cart-item]');
    const input = item?.querySelector('[data-cart-quantity]');
    if (!item || !input) return;

    const bounds = this.getQuantityBounds(input);
    const current = this.getInputQuantity(input, bounds.min);
    const direction = button.dataset.action === 'increase' ? 1 : -1;
    const next = this.normalizeQuantity(current + direction * bounds.step, bounds);

    if (next === current) {
      this.syncQuantityButtons(item, current);
      return;
    }

    input.value = String(next);
    this.syncQuantityButtons(item, next);
    this.debounceQuantityChange(item.dataset.lineKey, next, input);
  }

  handleQuantityInput(input) {
    const item = input.closest('[data-cart-item]');
    if (!item) return;

    const bounds = this.getQuantityBounds(input);
    const entered = this.getInputQuantity(input, bounds.min);
    const quantity = this.normalizeQuantity(entered, bounds);

    input.value = String(quantity);
    this.syncQuantityButtons(item, quantity);
    this.debounceQuantityChange(item.dataset.lineKey, quantity, input);
  }

  getQuantityBounds(input) {
    const min = Number.parseInt(input.min, 10);
    const max = Number.parseInt(input.max, 10);
    const step = Number.parseInt(input.step, 10);

    return {
      min: Number.isNaN(min) ? 1 : min,
      max: Number.isNaN(max) ? Number.POSITIVE_INFINITY : max,
      step: Number.isNaN(step) || step <= 0 ? 1 : step,
    };
  }

  getInputQuantity(input, fallback = 1) {
    const quantity = Number.parseInt(input.value, 10);
    return Number.isNaN(quantity) ? fallback : quantity;
  }

  normalizeQuantity(quantity, bounds) {
    let normalized = Number.isFinite(quantity) ? quantity : bounds.min;

    normalized = Math.max(bounds.min, normalized);
    if (Number.isFinite(bounds.max)) normalized = Math.min(bounds.max, normalized);

    const stepCount = Math.floor((normalized - bounds.min) / bounds.step);
    normalized = bounds.min + Math.max(0, stepCount) * bounds.step;

    if (Number.isFinite(bounds.max)) normalized = Math.min(bounds.max, normalized);
    return normalized;
  }

  syncQuantityButtons(item, quantity = null) {
    const input = item?.querySelector('[data-cart-quantity]');
    if (!input) return;

    const bounds = this.getQuantityBounds(input);
    const current = quantity ?? this.getInputQuantity(input, bounds.min);

    const decrease = item.querySelector(
      '[data-cart-quantity-button][data-action="decrease"]',
    );
    const increase = item.querySelector(
      '[data-cart-quantity-button][data-action="increase"]',
    );

    if (decrease) {
      const disabled = current <= bounds.min;
      decrease.toggleAttribute('disabled', disabled);
      decrease.setAttribute('aria-disabled', String(disabled));
    }

    if (increase) {
      const disabled = Number.isFinite(bounds.max) && current >= bounds.max;
      increase.toggleAttribute('disabled', disabled);
      increase.setAttribute('aria-disabled', String(disabled));
    }
  }

  syncAllQuantityButtons() {
    this.querySelectorAll('[data-cart-item]').forEach((item) => {
      this.syncQuantityButtons(item);
    });
  }

  debounceQuantityChange(lineKey, quantity, control) {
    if (!lineKey) return;

    window.clearTimeout(this.quantityTimers.get(lineKey));

    const timer = window.setTimeout(() => {
      this.quantityTimers.delete(lineKey);
      this.changeLine(lineKey, quantity, control);
    }, 350);

    this.quantityTimers.set(lineKey, timer);
  }

  async changeLine(lineKey, quantity, control) {
    if (!lineKey) return;

    window.clearTimeout(this.quantityTimers.get(lineKey));
    this.quantityTimers.delete(lineKey);
    this.setLoading(control, true);
    this.setBusy(true);

    try {
      const response = await this.postJSON(this.dataset.cartChangeUrl, {
        id: lineKey,
        quantity,
        sections: [this.sectionId],
        sections_url: this.dataset.cartUrl,
      });

      this.renderSection(response);
      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);
      await this.refresh();
    } finally {
      this.setLoading(control, false);
      this.setBusy(false);
    }
  }

  async applyDiscount(form) {
    const input = form.querySelector('[data-cart-discount-input]');
    const submit = form.querySelector('[type="submit"]');
    const code = input?.value.trim() || '';

    if (!code) {
      input?.focus();
      return;
    }

    const codes = this.getAppliedDiscountCodes();
    if (!codes.some((currentCode) => currentCode.toLowerCase() === code.toLowerCase())) {
      codes.push(code);
    }

    this.setLoading(submit, true);
    this.setBusy(true);

    try {
      const response = await this.updateDiscounts(codes);
      const applied = this.hasAppliedDiscount(response, code);

      this.renderSection(response, {
        status: applied
          ? this.dataset.discountSuccess
          : this.dataset.discountInvalid,
        isError: !applied,
        discountValue: applied ? '' : code,
      });

      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(submit, false);
      this.setBusy(false);
    }
  }

  async removeDiscount(button) {
    const codeToRemove = button.dataset.cartDiscountCode || '';
    const remainingCodes = this.getAppliedDiscountCodes().filter(
      (code) => code.toLowerCase() !== codeToRemove.toLowerCase(),
    );

    this.setLoading(button, true);
    this.setBusy(true);

    try {
      const response = await this.updateDiscounts(remainingCodes);

      this.renderSection(response, {
        status: this.dataset.discountRemoved,
      });

      this.dispatchCartUpdated(response);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoading(button, false);
      this.setBusy(false);
    }
  }

  updateDiscounts(codes) {
    return this.postJSON(this.dataset.cartUpdateUrl, {
      discount: codes.join(','),
      sections: [this.sectionId],
      sections_url: this.dataset.cartUrl,
    });
  }

  getAppliedDiscountCodes() {
    return Array.from(this.querySelectorAll('[data-cart-discount-code]'))
      .map((element) => element.dataset.cartDiscountCode?.trim())
      .filter(Boolean);
  }

  hasAppliedDiscount(cart, code) {
    const normalizedCode = code.trim().toLowerCase();
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map(
          (allocation) => allocation.discount_application,
        ),
      ),
    ];

    return applications.some(
      (application) => application?.title?.trim().toLowerCase() === normalizedCode,
    );
  }

  validateTerms() {
    const terms = this.querySelector('[data-cart-terms]');
    if (!terms || terms.checked) return true;

    terms.focus();
    this.setStatus(this.dataset.termsError, true);
    return false;
  }

  async refresh() {
    const url = new URL(this.dataset.cartUrl, window.location.origin);
    url.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw await this.createRequestError(response);

      const sections = await response.json();
      this.renderSection({ sections });
    } catch (error) {
      this.handleError(error);
    }
  }

  renderSection(response, options = {}) {
    const sectionHTML = response?.sections?.[this.sectionId];
    if (!sectionHTML) return;

    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextCart = parsed.querySelector('main-cart');
    if (!nextCart) return;

    const scrollTop = window.scrollY;
    this.updateCartCount(response.item_count ?? Number(nextCart.dataset.cartItemCount || 0));
    this.replaceWith(nextCart);

    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollTop, behavior: 'auto' });

      if (options.discountValue) {
        const input = nextCart.querySelector('[data-cart-discount-input]');
        if (input) input.value = options.discountValue;
      }

      if (options.status) {
        nextCart.setStatus?.(options.status, options.isError);
      }
    });
  }

  updateCartCount(count) {
    const normalized = Number.parseInt(count, 10) || 0;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(normalized);
      element.hidden = normalized === 0;
    });

    document.querySelectorAll('[data-cart-count-text]').forEach((element) => {
      const template = element.dataset.cartCountTextTemplate;
      element.textContent = template
        ? template.replace('[count]', String(normalized))
        : String(normalized);
    });
  }

  dispatchCartUpdated(response) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          response,
          cart: response,
          source: 'main-cart',
          open: false,
        },
      }),
    );
  }

  initializeTimer() {
    const output = this.querySelector('[data-cart-countdown]');
    const duration = Number.parseInt(this.dataset.cartTimerSeconds, 10);
    const storageKey = this.dataset.cartTimerKey;

    if (!output || !Number.isFinite(duration) || duration <= 0 || !storageKey) return;

    let endTime = Number.parseInt(window.sessionStorage.getItem(storageKey), 10);

    if (!Number.isFinite(endTime) || endTime <= Date.now()) {
      endTime = Date.now() + duration * 1000;
      window.sessionStorage.setItem(storageKey, String(endTime));
    }

    const update = () => {
      const remainingSeconds = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;

      output.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

      if (remainingSeconds === 0 && this.timerInterval) {
        window.clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    };

    update();
    this.timerInterval = window.setInterval(update, 1000);
  }

  async postJSON(url, body) {
    if (!url) throw new Error(this.dataset.genericError || 'Missing cart endpoint.');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw await this.createRequestError(response);
    return response.json();
  }

  async createRequestError(response) {
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    const fallback = this.dataset.genericError || 'Something went wrong. Please try again.';
    return new Error(payload?.description || payload?.message || `${fallback} (${response.status})`);
  }

  setLoading(control, loading) {
    if (!(control instanceof HTMLElement)) return;

    control.toggleAttribute('disabled', loading);
    control.setAttribute('aria-busy', String(loading));
    control.classList.toggle('is-loading', loading);

    const textElement = control.querySelector('[data-discount-text], [data-remove-text], [data-qty-icon]');
    if (textElement) {
      textElement.style.opacity = loading ? '0' : '1';
    }
  }

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    this.classList.toggle('is-updating', busy);
  }

  setStatus(message, isError = false) {
    const status = this.querySelector('[data-cart-page-status]');
    if (!status) return;

    status.textContent = message || '';
    status.dataset.status = isError ? 'error' : 'success';
  }

  handleError(error) {
    console.error(error);
    this.setStatus(error?.message || this.dataset.genericError, true);
  }
}

if (!customElements.get('main-cart')) {
  customElements.define('main-cart', MainCart);
}
